import { api } from 'store/api'
import { UpdateInferenceDataListener } from 'store/socket/event'
import { extractImage, InferenceFallback, InferenceListener } from 'store/socket/listener'
import { socket } from 'store/socket/socket'
import { store } from 'store/store'
import { delay } from 'util/promise'

type L = InferenceListener
type F = InferenceFallback
type E = UpdateInferenceDataListener

const ACK_TIMEOUT = 10/*sec*/ * 1000
const POLL_DELAY = 10/*sec*/ * 1000
const POLL_TIMEOUT = 10/*min*/ * 60/*sec*/ * 1000

export function openInferenceChannel(inference_id: string, listener: L, fallback: F): void {
  const startFallback = async () => {
    const start = Date.now()
    while (true) {
      await delay(POLL_DELAY)
      const expired = Date.now() - start > POLL_TIMEOUT
      if (expired) return
      try {
        const json = await api.getInference(inference_id)
        fallback(json)
        if (json.status === 'complete') return
      } catch (e) {
        console.error('inference polling failed', e)
      }
    }
  }

  socket.use()
  const ws = socket.socket
  if (!ws) {
    console.error('no socket')
    return void startFallback()
  }

  const close = () => {
    ws.off('disconnect', onDisconnect)
    ws.off('update-inference-data', onEvent)
    socket.unuse()
  }

  const onDisconnect = (): void => {
    console.error('socket disconnected')
    close()
    void startFallback()
  }

  const onEvent: E = async (id, status, images_): Promise<void> => {
    if (inference_id !== id) return
    if (['complete', 'error'].includes(status)) close()
    const images = images_?.map(extractImage) ?? []
    listener({ inference_id, status, images })
  }

  const open = async (): Promise<void> => {
    try {
      await ws.timeout(ACK_TIMEOUT).emitWithAck('inference_id', inference_id)
      ws.on('disconnect', onDisconnect)
      ws.on('update-inference-data', onEvent)
    } catch (e) {
      console.error('socket emit failed', e)
      close()
      return void startFallback()
    }
  }

  void open()
}
